import React, { ReactElement } from "react"
import { BasicFormula } from "src/features/calculator/components/formula/BasicFormula"
import { HousePartnerFormula } from "src/features/calculator/components/formula/HousePartnerFormula"
import { HanaInternationalFormula } from "src/features/calculator/components/formula/HanaInternationalFormula"
import { AlphaBrainFormula } from "src/features/calculator/components/formula/AlphaBrainFormula"
import { DiscountFormula } from "src/features/calculator/components/formula/DiscountFormula"
import { FullReimbursementFormula } from "src/features/calculator/components/formula/FullReimbursementFormula"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"
import { CalculationSchema } from "src/features/calculator/schema"
import { ReMaxRevoFormula } from "src/features/calculator/components/formula/ReMaxRevoFormula"
import { RecipientFormula } from "src/features/calculator/components/formula/RecipientFormula"
import { RiahouseFormula } from "src/features/calculator/components/formula/RiahouseFormula"
import { SFitFormula } from "src/features/calculator/components/formula/SFitFormula"
import { ThomasLivingFormula } from "src/features/calculator/components/formula/ThomasLivingFormula"
import { AnybodyFormula } from "src/features/calculator/components/formula/AnybodyFormula"
import { ECompanyFormula } from "src/features/calculator/components/formula/ECompanyFormula"
import { ElitzFormula } from "src/features/calculator/components/formula/ElitzFormula"
import { GlobalEstateFormula } from "src/features/calculator/components/formula/GlobalEstateFormula"
import { strategyValueObject } from "src/features/calculator/utils/calculator"
import { HouseCorporationFormula } from "./HouseCorporationFormula"

type Props = {
  setValue: UseFormSetValue<CalculationSchema>
  watch: UseFormWatch<CalculationSchema>
  errors: FieldErrors<CalculationSchema>
  strategy: string
}

export const Formulas = ({ setValue, watch, errors, strategy }: Props): ReactElement => {
  if (!strategy) {
    return null
  }

  const formulas = {
    [strategyValueObject.basic]: <BasicFormula setValue={setValue} watch={watch} errors={errors} />,
    [strategyValueObject.feeDiscount]: (
      <DiscountFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.fullReimbursement]: (
      <FullReimbursementFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.jukyakuTaxIncluded30Percent]: (
      <RecipientFormula setValue={setValue} watch={watch} errors={errors} percent={30} />
    ),
    [strategyValueObject.jukyakuTaxIncluded16_5Percent]: (
      <RecipientFormula setValue={setValue} watch={watch} errors={errors} percent={16.5} />
    ),
    [strategyValueObject.jukyakuTaxIncluded0Percent]: (
      <RecipientFormula setValue={setValue} watch={watch} errors={errors} percent={0} />
    ),
    [strategyValueObject.alphaBrain]: (
      <AlphaBrainFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.anybodyWithAd]: (
      <AnybodyFormula setValue={setValue} watch={watch} errors={errors} withAd />
    ),
    [strategyValueObject.anybodyWithoutAd]: (
      <AnybodyFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.eCompany]: (
      <ECompanyFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.elitz]: <ElitzFormula setValue={setValue} watch={watch} errors={errors} />,
    [strategyValueObject.globalEstate]: (
      <GlobalEstateFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.hanaInternational]: (
      <HanaInternationalFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.reMaxRevoWithoutAd]: (
      <ReMaxRevoFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.housePartner]: (
      <HousePartnerFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.riaHouseWithoutAd]: (
      <RiahouseFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.riaHouseWithAd]: (
      <RiahouseFormula setValue={setValue} watch={watch} errors={errors} withAd />
    ),
    [strategyValueObject.sFit]: <SFitFormula setValue={setValue} watch={watch} errors={errors} />,
    [strategyValueObject.thomasLiving]: (
      <ThomasLivingFormula setValue={setValue} watch={watch} errors={errors} />
    ),
    [strategyValueObject.houseCorporation]: (
      <HouseCorporationFormula setValue={setValue} watch={watch} errors={errors} />
    ),
  }

  return formulas[strategy as keyof typeof formulas]
}
